import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, CalendarIcon, AccountIcon, ArrowDown, ThreeDotsIcon, EditPencil, DeleteICon, ArrowRight, SearchIcon, FilterIcon, CloseIcon } from '../../../public/icons';
import AddBlogModal from '../../../component/admin/blog/AddBlogModal';
import Pagination from '../../../component/shared/Pagination';
import SearchAndFilter from '../../../component/shared/SearchAndFilter';
import adminBlogService from '../../../services/admin/admin-blogServices';
import { useToast } from '../../../context/ToastContext';
import AdminLayout from '../layout';

// Stats Card Component
const StatsCard = ({ count, label, icon, bgColor, isLoading }) => {
  return (
    <div className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm flex-1 min-w-[150px] sm:min-w-[200px]">
      <div className="text-left">
        <div className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${bgColor}`}>
          {icon}
        </div>
        <div>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C2A46] mt-2 sm:mt-4">
            {isLoading ? (
              <span className="inline-block w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded animate-pulse"></span>
            ) : (
              count
            )}
          </p>
          <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Blog Table Component
const BlogTable = ({ blogs, onBlogAction }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeStatusDropdown, setActiveStatusDropdown] = useState(null);

  const getStatusBadge = (status) => {
    const config = adminBlogService.getStatusBadgeConfig(status);
    return (
      <span className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs leading-5 font-semibold rounded-full ${config.bgColor} whitespace-nowrap`}>
        <span className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${config.dotColor}`} />
        <span className={config.textColor}>
          {status}
        </span>
        <span>
          <ArrowDown size={10} />
        </span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    return adminBlogService.formatDate(dateString);
  };

  const formatViews = (views) => {
    return adminBlogService.formatViews(views);
  };

  const handleActionClick = (blogId, action, event) => {
    event.stopPropagation();
    setActiveDropdown(null);
    setActiveStatusDropdown(null);
    onBlogAction(blogId, action);
  };

  const handleStatusChange = (blogId, newStatus, event) => {
    event.stopPropagation();
    setActiveStatusDropdown(null);
    onBlogAction(blogId, 'toggle-status', newStatus);
  };

  const statusOptions = ['Published', 'Draft', 'Scheduled'];

  return (
    <div className="overflow-x-auto text-left">
      <table className="min-w-[700px] md:min-w-full divide-y divide-[#DBDBDB]">
        <thead className="bg-[#F3F4F6]">
          <tr>
            <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
              Blog Post
            </th>
            <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
              Author
            </th>
            <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
              Publish Date
            </th>
            <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
              Views
            </th>
            <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
              Status
            </th>
            <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {blogs.map((blog) => (
            <tr key={blog._id} className="hover:bg-gray-50 transition-colors duration-150">
              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <img
                    src={blog.featuredImage}
                    alt={blog.title}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm font-medium text-[#0E1628] truncate max-w-[150px] sm:max-w-xs">
                      {blog.title}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <AccountIcon size={12} className="text-[#6B635D]" />
                  <span className="text-xs sm:text-sm font-normal text-[#0E1628]">{blog.author}</span>
                </div>
              </td>
              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <CalendarIcon size={12} className="text-[#6B635D]" />
                  <span className="text-xs sm:text-sm font-normal text-[#0E1628]">{formatDate(blog.publishDate || blog.createdAt)}</span>
                </div>
              </td>
              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <span className="text-xs sm:text-sm font-normal text-[#0E1628]">{formatViews(blog.views)}</span>
                </div>
               </td>
              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveStatusDropdown(activeStatusDropdown === blog._id ? null : blog._id);
                    setActiveDropdown(null);
                  }}
                  className="focus:outline-none"
                >
                  {getStatusBadge(blog.status)}
                </button>
                
                {activeStatusDropdown === blog._id && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setActiveStatusDropdown(null)}
                    />
                    <div className="absolute left-0 top-8 mt-2 w-32 sm:w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      {statusOptions.map((status) => (
                        <button
                          key={status}
                          onClick={(e) => handleStatusChange(blog._id, status, e)}
                          className={`block w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm hover:bg-gray-50 ${
                            blog.status === status ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </>
                )}
               </td>
              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveDropdown(activeDropdown === blog._id ? null : blog._id);
                    setActiveStatusDropdown(null);
                  }}
                  className="text-[#6B635D] hover:text-[#1C2A46] transition-colors p-1"
                >
                  <ThreeDotsIcon className='w-4 sm:w-5' />
                </button>
                
                {activeDropdown === blog._id && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setActiveDropdown(null)}
                    />
                    <div className="absolute right-0 top-8 mt-2 w-40 sm:w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                      <button
                        onClick={(e) => handleActionClick(blog._id, 'edit', e)}
                        className="flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                      >
                        <EditPencil size={12} />
                        <span>Edit Blog</span>
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={(e) => handleActionClick(blog._id, 'delete', e)}
                        className="flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 w-full text-left"
                      >
                        <DeleteICon size={12} />
                        <span>Delete Blog</span>
                      </button>
                    </div>
                  </>
                )}
               </td>
             </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Mobile Filter Modal
const MobileFiltersModal = ({ isOpen, onClose, searchQuery, setSearchQuery, statusFilter, setStatusFilter, statusOptions }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={onClose} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    setStatusFilter(option);
                    onClose();
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === option
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button 
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('All status');
              onClose();
            }}
            className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
          <button 
            onClick={onClose}
            className="flex-1 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

// Main Blogs Tab Component
const BlogsTab = ({ onBlogSelect, refreshTrigger }) => {
  const { success, error } = useToast();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [stats, setStats] = useState({
    totalBlogs: 0,
    publishedBlogs: 0,
    draftBlogs: 0,
    scheduledBlogs: 0,
    totalViews: 0,
    totalLikes: 0,
    categories: []
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All status');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    totalItems: 0
  });

  const statusOptions = ['All status', 'Published', 'Draft', 'Scheduled'];
  const categoryOptions = ['All categories', 'Memory Keeping', 'DIY & Craft', 'Stories', 'Travel', 'Culture', 'Food', 'Art', 'History', 'Lifestyle'];

  const fetchBlogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const params = {
        page,
        limit: pagination.itemsPerPage,
        search: searchQuery || undefined,
        status: statusFilter !== 'All status' ? statusFilter : undefined,
        sort: '-publishDate'
      };

      const response = await adminBlogService.getBlogs(params);
      
      if (response.success) {
        setBlogs(response.data.blogs);
        setPagination(prev => ({
          ...prev,
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.pages,
          totalItems: response.data.pagination.total
        }));
      } else {
        setBlogs([]);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      error('Failed to fetch blogs');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, pagination.itemsPerPage, error]);

  const fetchStatistics = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await adminBlogService.getBlogStats();
      
      if (response.success) {
        setStats({
          totalBlogs: response.data.totalBlogs || 0,
          publishedBlogs: response.data.publishedBlogs || 0,
          draftBlogs: response.data.draftBlogs || 0,
          scheduledBlogs: response.data.scheduledBlogs || 0,
          totalViews: response.data.totalViews || 0,
          totalLikes: response.data.totalLikes || 0,
          categories: response.data.categories || []
        });
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs(pagination.currentPage);
    fetchStatistics();
  }, [fetchBlogs, fetchStatistics, pagination.currentPage, refreshTrigger]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.currentPage === 1) {
        fetchBlogs(1);
      } else {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, fetchBlogs]);

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
  };

  const handleBlogAction = async (blogId, action, newStatus = null) => {
    try {
      switch (action) {
        case 'view':
          const blog = blogs.find(b => b._id === blogId);
          if (blog && onBlogSelect) {
            onBlogSelect(blog);
          } else {
            const response = await adminBlogService.getBlogById(blogId);
            if (response.success && onBlogSelect) {
              onBlogSelect(response.data);
            }
          }
          break;
        
        case 'edit':
          const blogToEdit = blogs.find(b => b._id === blogId);
          setEditingBlog(blogToEdit);
          setShowAddModal(true);
          break;
        
        case 'toggle-status':
          const response = await adminBlogService.updateBlogStatus(blogId, newStatus);
          
          if (response.success) {
            success(`Blog status updated to ${newStatus}`);
            fetchBlogs(pagination.currentPage);
            fetchStatistics();
          }
          break;
        
        case 'delete':
          if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
            const deleteResponse = await adminBlogService.deleteBlog(blogId);
            
            if (deleteResponse.success) {
              success('Blog deleted successfully');
              fetchBlogs(pagination.currentPage);
              fetchStatistics();
            }
          }
          break;
        
        default:
          console.log('Action:', action, 'for blog:', blogId);
      }
    } catch (err) {
      console.error('Blog action error:', err);
      error(err.response?.data?.message || 'Action failed');
    }
  };

  const handleSubmitBlog = async (blogData) => {
    try {
      await adminBlogService.createBlog(blogData);
      success('Blog published successfully!');
      fetchBlogs(pagination.currentPage);
      fetchStatistics();
      setShowAddModal(false);
      setEditingBlog(null);
    } catch (err) {
      console.error('Error submitting blog:', err);
      const errorMessage = err.response?.data?.message || 'Failed to publish blog';
      error(errorMessage);
    }
  };

  const handleSaveDraft = async (blogData) => {
    try {
      const draftData = { ...blogData, status: 'Draft' };
      await adminBlogService.createBlog(draftData);
      success('Draft saved successfully!');
      fetchBlogs(pagination.currentPage);
      fetchStatistics();
      setShowAddModal(false);
      setEditingBlog(null);
    } catch (err) {
      console.error('Error saving draft:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save draft';
      error(errorMessage);
    }
  };

  const handleUpdateBlog = async (blogData) => {
    try {
      await adminBlogService.updateBlog(editingBlog._id, blogData);
      success('Blog updated successfully!');
      fetchBlogs(pagination.currentPage);
      fetchStatistics();
      setShowAddModal(false);
      setEditingBlog(null);
    } catch (err) {
      console.error('Error updating blog:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update blog';
      error(errorMessage);
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingBlog(null);
  };

  const statsData = [
    {
      count: stats.totalBlogs,
      label: 'Total Blogs',
      icon: <span className="text-white text-xs font-bold">B</span>,
      bgColor: 'bg-[#2F8F5B]',
      isLoading: statsLoading
    },
    {
      count: stats.publishedBlogs,
      label: 'Published',
      icon: <span className="text-white text-xs font-bold">P</span>,
      bgColor: 'bg-[#412F8F]',
      isLoading: statsLoading
    },
    {
      count: stats.draftBlogs,
      label: 'Drafts',
      icon: <span className="text-white text-xs font-bold">D</span>,
      bgColor: 'bg-[#8F342F]',
      isLoading: statsLoading
    },
    {
      count: stats.scheduledBlogs,
      label: 'Scheduled',
      icon: <span className="text-white text-xs font-bold">S</span>,
      bgColor: 'bg-[#D2A146]',
      isLoading: statsLoading
    }
  ];

  if (loading && blogs.length === 0) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#55000C]"></div>
          <span className="ml-3 text-gray-600 text-sm">Loading blogs...</span>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {statsData.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>

        {/* Blogs Section */}
        <div className="bg-white rounded-sm shadow-sm">
          {/* Header */}
          <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-[#DBDBDB]">
            {/* Desktop view */}
            <div className="hidden md:flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">All Blog Posts</h2>
              
              <div className="flex gap-3 lg:gap-4 items-center">
                {/* Search Box */}
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <SearchIcon size={18} className="text-[#6B635D]" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search blogs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="p-2 pl-9 pr-3 border w-48 lg:w-52 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  />
                </div>

                {/* Status Filter */}
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 pl-3 pr-7 border w-32 lg:w-36 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  >
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ArrowDown size={14} className="text-[#6B635D]" />
                  </div>
                </div>

                {/* Add Blog Button */}
                <button
                  onClick={() => {
                    setEditingBlog(null);
                    setShowAddModal(true);
                  }}
                  disabled={loading}
                  className="px-3 lg:px-5 py-2.5 sm:py-3.5 bg-[#D2A146] text-white text-xs sm:text-sm font-medium hover:bg-[#C19654] transition-colors flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <PlusIcon size={14} color='#fff' />
                  Create New Blog
                </button>
              </div>
            </div>

            {/* Mobile view */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-medium text-[#0E1628]">All Blog Posts</h2>
                <button 
                  onClick={() => setMobileFiltersOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
                >
                  <FilterIcon size={14} />
                  Filters
                </button>
              </div>
              
              {/* Mobile Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={16} className="text-[#6B635D]" />
                </div>
                <input
                  type="text"
                  placeholder="Search blogs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
                />
              </div>

              {/* Mobile Create Button */}
              <button
                onClick={() => {
                  setEditingBlog(null);
                  setShowAddModal(true);
                }}
                disabled={loading}
                className="w-full mt-3 py-2.5 bg-[#D2A146] text-white text-sm font-medium hover:bg-[#C19654] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed rounded"
              >
                <PlusIcon size={16} color='#fff' />
                Create New Blog
              </button>
            </div>
          </div>

          {/* Blogs Table */}
          <BlogTable 
            blogs={blogs}
            onBlogAction={handleBlogAction}
          />

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-t border-[#DBDBDB]">
              <div className="flex justify-center">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          )}

          {/* Empty State */}
          {blogs.length === 0 && !loading && (
            <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
              <div className="text-gray-400 mb-2 text-sm">No blog posts found</div>
              <div className="text-xs sm:text-sm text-gray-500">
                {searchQuery || statusFilter !== 'All status' 
                  ? 'Try adjusting your search or filters'
                  : 'Create your first blog post to get started'}
              </div>
              {!searchQuery && statusFilter === 'All status' && (
                <button
                  onClick={() => {
                    setEditingBlog(null);
                    setShowAddModal(true);
                  }}
                  className="mt-4 px-4 py-2 bg-[#D2A146] text-white text-sm hover:bg-[#C19654] transition-colors inline-flex items-center gap-2 rounded"
                >
                  <PlusIcon size={16} />
                  Create Your First Blog
                </button>
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Blog Modal */}
        <AddBlogModal
          isOpen={showAddModal}
          onClose={handleModalClose}
          onSubmit={handleSubmitBlog}
          onSaveDraft={handleSaveDraft}
          onUpdate={handleUpdateBlog}
          blogToEdit={editingBlog}
        />

        {/* Mobile Filters Modal */}
        <MobileFiltersModal 
          isOpen={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          statusOptions={statusOptions}
        />
      </div>
    </AdminLayout>
  );
};

export default BlogsTab;